import { useEffect, useState } from 'react';
import { Mail, MessageSquare, Bell, Save, ChevronDown, ChevronRight, Smartphone, Workflow as WorkflowIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { SelectField } from '@/components/ui/SelectField';

type Channel = 'email' | 'whatsapp' | 'sms' | 'inApp';

interface CommsEvent {
  key: string;
  label: string;
  description: string;
  channels: Channel[];
  placeholders: string[];
}

interface ChannelCfg { enabled?: boolean; templateId?: string | null; templateName?: string | null }
type EventCfg = Partial<Record<Channel, ChannelCfg>>;

const CHANNEL_META: Record<Channel, { label: string; icon: any }> = {
  email: { label: 'Email', icon: Mail },
  whatsapp: { label: 'WhatsApp', icon: MessageSquare },
  sms: { label: 'SMS', icon: Smartphone },
  inApp: { label: 'In-app & push', icon: Bell },
};

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${on ? 'bg-indigo-500' : 'bg-slate-300'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );
}

export function CommunicationsSettings() {
  const [events, setEvents] = useState<CommsEvent[]>([]);
  const [config, setConfig] = useState<Record<string, EventCfg>>({});
  const [emailTemplates, setEmailTemplates] = useState<{ id: string; name: string }[]>([]);
  const [smsTemplates, setSmsTemplates] = useState<{ id: string; name: string }[]>([]);
  const [waTemplates, setWaTemplates] = useState<string[]>([]);
  const [waTemplateObjs, setWaTemplateObjs] = useState<{ id: string; name: string }[]>([]);
  const [waAccountId, setWaAccountId] = useState<string | null>(null);
  const [reengagementTplId, setReengagementTplId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Which event cards are expanded. Set of event keys — supports
  // multiple open at once instead of accordion-style single-open, which
  // was flagged as annoying when comparing two events side by side.
  // Default: all collapsed so the tab opens compact and the user
  // clicks into the specific event they care about.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    Promise.all([
      api.get('/settings/communications').then(r => r.data).catch(() => ({ events: [], config: {} })),
      api.get('/email-templates').then(r => r.data).catch(() => []),
      api.get('/sms-templates').then(r => r.data).catch(() => []),
      api.get('/whatsapp/templates').then(r => r.data).catch(() => []),
      api.get('/whatsapp/accounts').then(r => r.data).catch(() => []),
    ]).then(([comms, et, st, wt, accts]) => {
      setEvents(comms.events || []);
      setConfig(comms.config || {});
      setEmailTemplates((Array.isArray(et) ? et : []).map((t: any) => ({ id: t.id, name: t.name })));
      setSmsTemplates((Array.isArray(st) ? st : []).filter((t: any) => t.isActive !== false).map((t: any) => ({ id: t.id, name: t.name })));
      const waList = Array.isArray(wt?.data) ? wt.data : Array.isArray(wt) ? wt : [];
      const approved = waList.filter((t: any) => String(t.status).toUpperCase() === 'APPROVED');
      setWaTemplates(approved.map((t: any) => t.name));
      setWaTemplateObjs(approved.map((t: any) => ({ id: t.id, name: t.name })));
      const accountsArr = Array.isArray(accts?.data) ? accts.data : Array.isArray(accts) ? accts : [];
      const acc = accountsArr[0];
      if (acc) {
        setWaAccountId(acc.id);
        setReengagementTplId(acc.defaultReengagementTemplateId ?? '');
      }
    }).finally(() => setLoading(false));
  }, []);

  const ch = (ek: string, c: Channel): ChannelCfg => config[ek]?.[c] ?? { enabled: true };
  const setCh = (ek: string, c: Channel, patch: ChannelCfg) =>
    setConfig(prev => ({ ...prev, [ek]: { ...prev[ek], [c]: { enabled: true, ...prev[ek]?.[c], ...patch } } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/settings/communications', { config });
      if (waAccountId) {
        await api.patch(`/whatsapp/accounts/${waAccountId}`, { defaultReengagementTemplateId: reengagementTplId || null });
      }
      toast('success', 'Communication settings saved');
    } catch {
      toast('error', 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-slate-500 max-w-xl">
          Choose which channels fire for each event, and which template each one uses.
          Leave a channel off to skip it. Empty template = the built-in default.
        </p>
        <button
          onClick={save}
          disabled={saving}
          className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {waAccountId && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900">WhatsApp re-engagement (opener) template</h3>
          <p className="mb-3 text-xs text-slate-500 max-w-xl">
            Sent automatically when a customer’s 24-hour WhatsApp reply window is closed, so the
            conversation can re-open. Pick an approved template (prefer one with no variables).
            Default uses “reengagement_message”.
          </p>
          <SelectField
            value={reengagementTplId}
            onChange={e => setReengagementTplId(e.target.value)}
            className="h-9 w-full max-w-md rounded-lg border border-slate-200 bg-white px-2 text-sm"
          >
            <option value="">Default — “reengagement_message”</option>
            {waTemplateObjs.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </SelectField>
        </div>
      )}

      {/* Lead, meeting and follow-up messages moved to Workflows. */}
      <div className="flex items-start gap-3 rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
        <WorkflowIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#6366F1]" />
        <div className="text-sm text-slate-700">
          <p className="font-medium text-slate-800">Lead, meeting and follow-up messages are now in Workflows</p>
          <p className="mt-0.5 text-slate-600">
            Welcome messages, new-lead alerts, meeting confirmations and follow-up reminders run as ready-made workflows you can edit, pause or extend.
          </p>
          <Link to="/workflows" className="mt-1.5 inline-block font-medium text-[#6366F1] hover:underline">Open Workflows →</Link>
        </div>
      </div>

      {events.map(ev => {
        const isOpen = expanded.has(ev.key);
        // Small summary hint next to the label when the card is closed
        // so admins can scan whether an event has any channel configured
        // without expanding it. Reads e.g. "Email · WhatsApp".
        const activeChannels = ev.channels
          .filter((c) => (config[ev.key]?.[c]?.enabled ?? true) !== false)
          .map((c) => CHANNEL_META[c].label);
        const summary = activeChannels.length > 0 ? activeChannels.join(' · ') : 'All channels off';
        return (
          <div key={ev.key} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {/* Clickable header — toggles the card. Whole header is the
                target so admins can hit anywhere, not just the chevron. */}
            <button
              type="button"
              onClick={() => toggleExpanded(ev.key)}
              className="w-full flex items-start gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
              aria-expanded={isOpen}
            >
              <span className="mt-0.5 text-slate-400 flex-shrink-0">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </span>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-slate-900">{ev.label}</h3>
                <p className="text-xs text-slate-500 leading-snug">{ev.description}</p>
              </div>
              {!isOpen && (
                <span className="text-[11px] font-medium text-slate-400 flex-shrink-0 mt-1">
                  {summary}
                </span>
              )}
            </button>

            {isOpen && (
              <div className="px-5 pb-5 pt-1">
                <div className="space-y-3">
                  {ev.channels.map(c => {
                    const cfg = ch(ev.key, c);
                    const on = cfg.enabled !== false;
                    const Meta = CHANNEL_META[c];
                    return (
                      <div key={c} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                        <Toggle on={on} onChange={v => setCh(ev.key, c, { enabled: v })} />
                        <Meta.icon className={`h-4 w-4 ${on ? 'text-slate-600' : 'text-slate-400'}`} />
                        <span className={`text-sm font-medium w-28 ${on ? 'text-slate-800' : 'text-slate-400'}`}>{Meta.label}</span>

                        {on && c === 'email' && (
                          <SelectField
                            value={cfg.templateId ?? ''}
                            onChange={e => setCh(ev.key, c, { templateId: e.target.value || null })}
                            className="flex-1 min-w-[180px] h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm"
                          >
                            <option value="">Built-in default template</option>
                            {emailTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </SelectField>
                        )}

                        {on && c === 'whatsapp' && (
                          <SelectField
                            value={cfg.templateName ?? ''}
                            onChange={e => setCh(ev.key, c, { templateName: e.target.value || null })}
                            className="flex-1 min-w-[180px] h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm"
                          >
                            <option value="">Default template</option>
                            {waTemplates.map(n => <option key={n} value={n}>{n}</option>)}
                          </SelectField>
                        )}

                        {on && c === 'sms' && (
                          <SelectField
                            value={cfg.templateId ?? ''}
                            onChange={e => setCh(ev.key, c, { templateId: e.target.value || null })}
                            className="flex-1 min-w-[180px] h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm"
                          >
                            <option value="">— pick an SMS template —</option>
                            {smsTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </SelectField>
                        )}

                        {on && c === 'inApp' && (
                          <span className="text-xs text-slate-400">Sends to admins/managers + their mobile</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-3 text-xs text-slate-400">
                  Placeholders: {ev.placeholders.map(p => <code key={p} className="mx-0.5 rounded bg-slate-100 px-1 py-0.5 text-slate-600">{`{{${p}}}`}</code>)}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
